// frontend/src/components/GPUMonitoring/GPUMonitoring.js
import React, { useEffect, useState, useCallback } from 'react';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  TimeScale
} from 'chart.js';
import apiClient from '../../apiClient';
import './GPUMonitoring.css';

// Registrera Chart.js komponenter
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  TimeScale
);

function GPUMonitoring() {
  // Real-time status
  const [realtimeStatus, setRealtimeStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  
  // Historical data
  const [hourlyData, setHourlyData] = useState([]);
  const [dailyData, setDailyData] = useState([]);
  const [capacityAnalysis, setCapacityAnalysis] = useState(null);
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(30); // seconds
  
  // Time range selection
  const [hourlyRange, setHourlyRange] = useState(24); // hours
  const [dailyRange, setDailyRange] = useState(30);   // days

  // Define fetchRealtimeStatus first
  const fetchRealtimeStatus = useCallback(async () => {
    if (!autoRefresh) setStatusLoading(true);
    try {
      const response = await apiClient.get('/api/monitoring/status');
      setRealtimeStatus(response.data);
      setError('');
    } catch (err) {
      console.error('Failed to fetch real-time status:', err);
      if (err.response?.status === 403) {
        setError('Ingen behörighet att visa GPU-monitoring');
      } else {
        setError('Kunde inte hämta GPU-status');
      }
    }
    if (!autoRefresh) setStatusLoading(false);
  }, [autoRefresh]);

  // Auto-refresh timer
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      fetchRealtimeStatus();
    }, refreshInterval * 1000);

    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval, fetchRealtimeStatus]);

  // Initial data load
  useEffect(() => {
    loadInitialData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load data when ranges change
  useEffect(() => {
    fetchHourlyData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hourlyRange]);

  useEffect(() => {
    fetchDailyData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dailyRange]);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchRealtimeStatus(),
        fetchHourlyData(),
        fetchDailyData(),
        fetchCapacityAnalysis()
      ]);
    } catch (err) {
      setError('Failed to load monitoring data');
    }
    setLoading(false);
  };

  const fetchHourlyData = async () => {
    try {
      const response = await apiClient.get(`/api/monitoring/hourly/${hourlyRange}`);
      setHourlyData(response.data.data || []);
    } catch (err) {
      console.error('Failed to fetch hourly data:', err);
    }
  };

  const fetchDailyData = async () => {
    try {
      const response = await apiClient.get(`/api/monitoring/daily/${dailyRange}`);
      setDailyData(response.data.data || []);
    } catch (err) {
      console.error('Failed to fetch daily data:', err);
    }
  };

  const fetchCapacityAnalysis = async () => {
    try {
      const response = await apiClient.get('/api/monitoring/capacity-analysis');
      setCapacityAnalysis(response.data);
    } catch (err) {
      console.error('Failed to fetch capacity analysis:', err);
    }
  };

  const fetchDebugInfo = async () => {
    try {
      const response = await apiClient.get('/api/monitoring/debug');
      console.log('Debug info:', response.data);
      alert('Debug info logged to console');
    } catch (err) {
      console.error('Failed to fetch debug info:', err);
      alert('Failed to fetch debug info. Check console for details.');
    }
  };

  // Format timestamp for display
  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleString('sv-SE');
  };

  // Get health status color
  const getHealthColor = (health) => {
    switch (health) {
      case 'good': return '#28a745';
      case 'warning': return '#ffc107';
      case 'critical': return '#dc3545';
      default: return '#6c757d';
    }
  };

  // Chart configurations
  const hourlyChartData = {
    labels: hourlyData.map(d => new Date(d.timestamp).toLocaleString('sv-SE', { 
      month: 'short', 
      day: 'numeric', 
      hour: '2-digit' 
    })),
    datasets: [
      {
        label: 'Genomsnittlig belastning (%)',
        data: hourlyData.map(d => d.avg_load),
        borderColor: 'rgb(75, 192, 192)',
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        tension: 0.4
      },
      {
        label: 'Max belastning (%)',
        data: hourlyData.map(d => d.max_load),
        borderColor: 'rgb(255, 99, 132)',
        backgroundColor: 'rgba(255, 99, 132, 0.2)',
        tension: 0.4
      }
    ]
  };

  const dailyChartData = {
    labels: dailyData.map(d => new Date(d.date).toLocaleDateString('sv-SE')),
    datasets: [
      {
        label: 'Genomsnittlig belastning (%)',
        data: dailyData.map(d => d.avg_load),
        backgroundColor: 'rgba(54, 162, 235, 0.5)',
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 1
      },
      {
        label: 'Toppbelastning (%)',
        data: dailyData.map(d => d.peak_load),
        backgroundColor: 'rgba(255, 206, 86, 0.5)',
        borderColor: 'rgba(255, 206, 86, 1)',
        borderWidth: 1
      }
    ]
  };

  const queueChartData = realtimeStatus ? {
    labels: ['Hög prioritet', 'Medium prioritet', 'Låg prioritet'],
    datasets: [{
      data: [
        realtimeStatus.queues?.high || 0,
        realtimeStatus.queues?.medium || 0,
        realtimeStatus.queues?.low || 0
      ],
      backgroundColor: [
        'rgba(220, 53, 69, 0.8)',
        'rgba(255, 193, 7, 0.8)',
        'rgba(40, 167, 69, 0.8)'
      ],
      borderColor: [
        'rgba(220, 53, 69, 1)',
        'rgba(255, 193, 7, 1)',
        'rgba(40, 167, 69, 1)'
      ],
      borderWidth: 2
    }]
  } : null;

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top'
      },
      title: {
        display: false
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100
      }
    }
  };

  const barChartOptions = {
    ...chartOptions,
    plugins: {
      ...chartOptions.plugins,
      title: {
        display: false
      }
    }
  };

  if (loading) {
    return (
      <div className="gpu-monitoring-container">
        <h2>GPU Server Monitoring</h2>
        <div className="loading-spinner">Laddar monitoring data...</div>
      </div>
    );
  }

  return (
    <div className="gpu-monitoring-container">
      <div className="monitoring-header">
        <h2>GPU Server Monitoring</h2>
        <div className="monitoring-controls">
          <label className="auto-refresh-control">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
            Auto-uppdatering ({refreshInterval}s)
          </label>
          <select 
            value={refreshInterval} 
            onChange={(e) => setRefreshInterval(Number(e.target.value))}
            disabled={!autoRefresh}
          >
            <option value={10}>10s</option>
            <option value={30}>30s</option>
            <option value={60}>60s</option>
            <option value={120}>2min</option>
          </select>
          <button onClick={fetchRealtimeStatus} disabled={statusLoading}>
            {statusLoading ? 'Uppdaterar...' : 'Uppdatera nu'}
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message">
          <i className="fas fa-exclamation-triangle"></i> {error}
        </div>
      )}

      {/* No Data Message */}
      {realtimeStatus && realtimeStatus.status === 'no_data' && (
        <div className="no-data-message">
          <div className="no-data-card">
            <i className="fas fa-chart-line" style={{fontSize: '48px', color: '#007bff', marginBottom: '15px'}}></i>
            <h3>Ingen GPU-data tillgänglig ännu</h3>
            <p>GPU-monitoring samlar in data kontinuerligt. Data kommer att visas här när:</p>
            <ul style={{textAlign: 'left', margin: '15px 0', color: '#666'}}>
              <li>GPU-servern används för transkribering</li>
              <li>Monitoring-systemet har samlat in tillräckligt med data</li>
              <li>Du har nödvändig behörighet för att se data</li>
            </ul>
            <div style={{display: 'flex', gap: '10px', marginTop: '15px', justifyContent: 'center'}}>
              <button onClick={fetchRealtimeStatus} disabled={statusLoading}>
                {statusLoading ? 'Kontrollerar...' : 'Kontrollera igen'}
              </button>
              <button onClick={fetchDebugInfo} disabled={statusLoading}>
                Debug info
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Real-time Status */}
      {realtimeStatus && realtimeStatus.status !== 'no_data' && (
        <div className="realtime-status">
          <h3>Aktuell Status</h3>
          <div className="status-cards">
            <div className="status-card gpu-card">
              <div className="card-header">
                <h4><i className="fas fa-microchip"></i> GPU Kapacitet</h4>
                <div 
                  className={`health-indicator ${realtimeStatus.health}`}
                  style={{ backgroundColor: getHealthColor(realtimeStatus.health) }}
                >
                  {realtimeStatus.health}
                </div>
              </div>
              <div className="card-body">
                <div className="metric">
                  <span className="metric-label">Använda slots:</span>
                  <span className="metric-value">
                    {realtimeStatus.gpu?.slots_used}/{realtimeStatus.gpu?.slots_total}
                  </span>
                </div>
                <div className="metric">
                  <span className="metric-label">Utnyttjande:</span>
                  <span className="metric-value">{realtimeStatus.gpu?.utilization_percent}%</span>
                </div>
                <div className="metric">
                  <span className="metric-label">Load Score:</span>
                  <span className="metric-value">{realtimeStatus.gpu?.load_score}/100</span>
                </div>
              </div>
            </div>

            <div className="status-card queue-card">
              <div className="card-header">
                <h4><i className="fas fa-list-ol"></i> Köer</h4>
              </div>
              <div className="card-body">
                <div className="metric">
                  <span className="metric-label">Total kö:</span>
                  <span className="metric-value">{realtimeStatus.queues?.total || 0}</span>
                </div>
                <div className="queue-breakdown">
                  <div className="queue-item high">Hög: {realtimeStatus.queues?.high || 0}</div>
                  <div className="queue-item medium">Medium: {realtimeStatus.queues?.medium || 0}</div>
                  <div className="queue-item low">Låg: {realtimeStatus.queues?.low || 0}</div>
                </div>
              </div>
            </div>

            <div className="status-card sessions-card">
              <div className="card-header">
                <h4><i className="fas fa-users"></i> Sessioner</h4>
              </div>
              <div className="card-body">
                <div className="metric">
                  <span className="metric-label">Aktiva:</span>
                  <span className="metric-value">{realtimeStatus.sessions?.active || 0}</span>
                </div>
                <div className="metric small">
                  <span className="metric-label">Senast uppdaterad:</span>
                  <span className="metric-value">
                    {formatTime(realtimeStatus.timestamp)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {queueChartData && (
            <div className="queue-chart">
              <h4>Köfördelning</h4>
              <div className="chart-container small">
                <Doughnut data={queueChartData} options={{ responsive: true, maintainAspectRatio: false }} />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Historical Charts */}
      <div className="historical-data">
        <div className="chart-section">
          <div className="section-header">
            <h3>Timvis belastning</h3>
            <select 
              value={hourlyRange} 
              onChange={(e) => setHourlyRange(Number(e.target.value))}
            >
              <option value={6}>6 timmar</option>
              <option value={24}>24 timmar</option>
              <option value={72}>3 dagar</option>
              <option value={168}>1 vecka</option>
            </select>
          </div>
          <div className="chart-container">
            <Line data={hourlyChartData} options={chartOptions} />
          </div>
        </div>

        <div className="chart-section">
          <div className="section-header">
            <h3>Daglig belastning</h3>
            <select 
              value={dailyRange} 
              onChange={(e) => setDailyRange(Number(e.target.value))}
            >
              <option value={7}>7 dagar</option>
              <option value={30}>30 dagar</option>
              <option value={90}>90 dagar</option>
            </select>
          </div>
          <div className="chart-container">
            <Bar data={dailyChartData} options={barChartOptions} />
          </div>
        </div>
      </div>

      {/* Capacity Analysis */}
      {capacityAnalysis && capacityAnalysis.status === 'success' && (
        <div className="capacity-analysis">
          <h3>Kapacitetsanalys</h3>
          <div className="analysis-metrics">
            <div className="analysis-card">
              <h4>Nuvarande trend (7 dagar)</h4>
              <div className="metric">
                <span className="metric-label">Snittbelastning:</span>
                <span className="metric-value">{capacityAnalysis.current_metrics?.avg_load_7_days}%</span>
              </div>
              <div className="metric">
                <span className="metric-label">Snittanvändare:</span>
                <span className="metric-value">{capacityAnalysis.current_metrics?.avg_users_7_days}</span>
              </div>
            </div>

            <div className="analysis-card">
              <h4>Tillväxttakt (per vecka)</h4>
              <div className="metric">
                <span className="metric-label">Belastningstillväxt:</span>
                <span className="metric-value">{capacityAnalysis.growth_rates?.load_growth_percent_per_week}%</span>
              </div>
              <div className="metric">
                <span className="metric-label">Användartillväxt:</span>
                <span className="metric-value">{capacityAnalysis.growth_rates?.user_growth_percent_per_week}%</span>
              </div>
            </div>

            {capacityAnalysis.projections && (
              <div className="analysis-card">
                <h4>Prognoser</h4>
                {capacityAnalysis.projections.weeks_to_80_percent && (
                  <div className="metric">
                    <span className="metric-label">80% kapacitet om:</span>
                    <span className="metric-value">{capacityAnalysis.projections.weeks_to_80_percent} veckor</span>
                  </div>
                )}
                {capacityAnalysis.projections.weeks_to_100_percent && (
                  <div className="metric warning">
                    <span className="metric-label">100% kapacitet om:</span>
                    <span className="metric-value">{capacityAnalysis.projections.weeks_to_100_percent} veckor</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {capacityAnalysis.recommendations && capacityAnalysis.recommendations.length > 0 && (
            <div className="recommendations">
              <h4>Rekommendationer</h4>
              {capacityAnalysis.recommendations.map((rec, index) => (
                <div key={index} className={`recommendation ${rec.type}`}>
                  <i className={`fas fa-${rec.type === 'urgent' ? 'exclamation-triangle' : rec.type === 'warning' ? 'exclamation-circle' : 'info-circle'}`}></i>
                  {rec.message}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default GPUMonitoring;